import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faFileLines } from "@fortawesome/free-solid-svg-icons";
import config from "../config/pyconfig";
import "./crossexam.css";

// ─────────────────────────────────────────────────────────────────────────────
// "Yes, read my documents" → prefill API
// NOTE: URL / body reference UI-la confirm pannunga — inga mattum maathunga.
// Response: { prefilled, case_type, case_type_reason, witnesses[], notes[], values{}, form{} }
// ─────────────────────────────────────────────────────────────────────────────
export const PREFILL_PATH = (uid) => `/cross-exam/${encodeURIComponent(uid)}/prefill`;
export const PREFILL_BODY = ({ sessionId, note, documentIds }) => ({
  session_id: sessionId,
  note: note || "",
  ...(documentIds?.length ? { document_ids: documentIds } : {}),
});

const prefillCrossExam = async (uid, args) => {
  const res = await fetch(`${config.apiBaseUrl}${PREFILL_PATH(uid)}`, {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: JSON.stringify(PREFILL_BODY(args)),
  });
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* ignore */
  }
  if (!res.ok) {
    const e = data?.error;
    const msg =
      (e && typeof e === "object" && e.message) ||
      (typeof data?.detail === "string" && data.detail) ||
      (typeof e === "string" && e) ||
      `${res.status} ${res.statusText}`;
    throw new Error(msg);
  }
  return data;
};

// ── form config per case type (prefill response `form` overrides these) ────
export const CASE_TYPES = [
  { key: "criminal", label: "Criminal" },
  { key: "civil", label: "Civil" },
  { key: "family", label: "Family" },
  { key: "consumer", label: "Consumer" },
  { key: "other", label: "Other" },
];

export const WITNESS_TYPES = [
  "eyewitness",
  "complainant / informant",
  "injured witness",
  "investigating officer",
  "medical officer",
  "forensic / scientific expert",
  "panch / seizure witness",
  "electronic evidence witness",
  "other",
];

const GENERAL_WITNESS_TYPES = [
  "party witness",
  "independent witness",
  "expert witness",
  "official witness",
  "document witness",
  "other",
];

export const HEADINGS = [
  "Facts and timeline",
  "Contradictions and omissions",
  "Identification of the accused",
  "Documentary and electronic evidence",
  "Medical and forensic evidence",
  "Credibility and reliability",
  "Suggestions (putting the case)",
];

const GENERAL_HEADINGS = [
  "Facts and timeline",
  "Contradictions and omissions",
  "Pleadings vs evidence",
  "Documentary and electronic evidence",
  "Credibility and reliability",
  "Suggestions (putting the case)",
];

const DEFAULT_FORMS = {
  criminal: {
    sides: [{ key: "defence", label: "Defence" }, { key: "prosecution", label: "Prosecution" }],
    witness_types: WITNESS_TYPES,
    headings: HEADINGS,
  },
  civil: {
    sides: [{ key: "defendant", label: "Defendant" }, { key: "plaintiff", label: "Plaintiff" }],
    witness_types: GENERAL_WITNESS_TYPES,
    headings: GENERAL_HEADINGS,
  },
  family: {
    sides: [{ key: "respondent", label: "Respondent" }, { key: "petitioner", label: "Petitioner" }],
    witness_types: GENERAL_WITNESS_TYPES,
    headings: GENERAL_HEADINGS,
  },
  consumer: {
    sides: [{ key: "opposite_party", label: "Opposite party" }, { key: "complainant", label: "Complainant" }],
    witness_types: GENERAL_WITNESS_TYPES,
    headings: GENERAL_HEADINGS,
  },
  other: {
    sides: [{ key: "respondent", label: "Respondent / defence" }, { key: "petitioner", label: "Petitioner / prosecution" }],
    witness_types: GENERAL_WITNESS_TYPES,
    headings: GENERAL_HEADINGS,
  },
};

const STYLES = [
  { value: "detailed", label: "Detailed" },
  { value: "concise", label: "Concise" },
];

const MIN_Q = 1;
const MAX_Q = 50;

const INITIAL_FORM = {
  caseType: "criminal",
  side: "",
  witness: "",
  witnessType: WITNESS_TYPES[0],
  objective: "",
  caseTheory: "",
  caseName: "",
  caseNumber: "",
  numQuestions: 15,
  style: STYLES[0].value,
  headings: [],
  instructions: "",
};

const labelOf = (list, key) => list.find((x) => x.key === key)?.label || key;

export const buildCrossExamPrompt = (f, formCfg) => {
  const lines = ["Prepare cross-examination questions."];
  lines.push(`Case type: ${labelOf(CASE_TYPES, f.caseType)}`);
  lines.push(`Witness: ${f.witness.trim()}`);
  lines.push(`Witness type: ${f.witnessType}`);
  lines.push(`Side conducting cross-examination: ${labelOf(formCfg.sides, f.side)}`);
  lines.push(`Objective: ${f.objective.trim()}`);
  if (f.caseTheory.trim()) lines.push(`Case theory: ${f.caseTheory.trim()}`);
  if (f.caseName.trim()) lines.push(`Case name: ${f.caseName.trim()}`);
  if (f.caseNumber.trim()) lines.push(`Case number: ${f.caseNumber.trim()}`);
  lines.push(`Number of questions: ${f.numQuestions}`);
  lines.push(`Style: ${f.style}`);

  const all = formCfg.headings;
  const headings = f.headings.length ? all.filter((h) => f.headings.includes(h)) : all;
  lines.push(`Headings: ${headings.join("; ")}`);
  if (f.instructions.trim()) lines.push(`Other details (not in the documents): ${f.instructions.trim()}`);

  return lines.join("\n");
};

// prefill `values` → form state
const fromPrefill = (data) => {
  const v = data?.values || {};
  return {
    ...INITIAL_FORM,
    caseType: v.case_type || data?.case_type || INITIAL_FORM.caseType,
    side: v.side || "",
    witness: v.witness || "",
    witnessType: v.witness_type || data?.form?.default_witness_type || INITIAL_FORM.witnessType,
    objective: v.objective || "",
    caseTheory: v.case_theory || "",
    caseName: v.case_name || "",
    caseNumber: v.case_number || "",
    numQuestions: v.num_questions || INITIAL_FORM.numQuestions,
    style: v.style || INITIAL_FORM.style,
    // ellaa heading-um tick aana → "none ticked = all" maadhiri empty vechukalaam
    headings:
      Array.isArray(v.headings) && data?.form?.headings && v.headings.length >= data.form.headings.length
        ? []
        : v.headings || [],
    instructions: v.instructions || "",
  };
};

/**
 * Props
 *  open, onClose, onSubmit(prompt) → false to keep the modal open
 *  userId, sessionId
 *  documents   [{ name }]  — files already in this chat (+ ready attachments)
 *  documentIds [id]        — optional, sent to the prefill API
 */
const CrossExamModal = ({ open, onClose, onSubmit, userId, sessionId, documents = [], documentIds = [] }) => {
  const [step, setStep] = useState("intro"); // intro | reading | form
  const [note, setNote] = useState("");
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [prefill, setPrefill] = useState(null);
  const [prefillError, setPrefillError] = useState("");
  const firstFieldRef = useRef(null);
  const noteRef = useRef(null);
  const reqRef = useRef(0); // prefill request id — pazhaya response-ai ignore panna

  const hasDocs = documents.length > 0;

  // Open aagum bodhu ellam reset
  useEffect(() => {
    if (!open) return;
    reqRef.current += 1;
    setStep("intro");
    setNote("");
    setForm(INITIAL_FORM);
    setErrors({});
    setPrefill(null);
    setPrefillError("");
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      if (step === "intro") noteRef.current?.focus();
      if (step === "form") firstFieldRef.current?.focus();
    }, 50);
    const onKey = (e) => e.key === "Escape" && close();
    document.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener("keydown", onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step]);

  // Case type-ku etha sides / witness types / headings
  const formCfg = useMemo(() => {
    const base = DEFAULT_FORMS[form.caseType] || DEFAULT_FORMS.other;
    const pf = prefill?.form;
    if (pf && pf.key === form.caseType) {
      return {
        sides: pf.sides?.length ? pf.sides : base.sides,
        witness_types: pf.witness_types?.length ? pf.witness_types : base.witness_types,
        headings: pf.headings?.length ? pf.headings : base.headings,
      };
    }
    return base;
  }, [form.caseType, prefill]);

  if (!open) return null;

  const close = () => {
    reqRef.current += 1;
    onClose();
  };

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const changeCaseType = (e) => {
    const caseType = e.target.value;
    const cfg = DEFAULT_FORMS[caseType] || DEFAULT_FORMS.other;
    setForm((prev) => ({
      ...prev,
      caseType,
      side: "",
      witnessType: cfg.witness_types.includes(prev.witnessType) ? prev.witnessType : cfg.witness_types[0],
      headings: [],
    }));
  };

  const toggleHeading = (h) =>
    setForm((prev) => ({
      ...prev,
      headings: prev.headings.includes(h) ? prev.headings.filter((x) => x !== h) : [...prev.headings, h],
    }));

  // Prefill-la vandha witness-ai click panna andha details fill aagum
  const pickWitness = (w) => {
    setForm((prev) => ({
      ...prev,
      witness: w.display || [w.label, w.name].filter(Boolean).join(" "),
      witnessType: w.witness_type || prev.witnessType,
      side: w.cross_examined_by || prev.side,
      objective: w.objectives?.[0] || prev.objective,
    }));
    setErrors({});
  };

  const fillMyself = () => {
    reqRef.current += 1; // reading nadandhaa adha cancel
    setForm((prev) => ({ ...prev, instructions: note.trim() || prev.instructions }));
    setStep("form");
  };

  const readDocuments = async () => {
    const id = ++reqRef.current;
    setStep("reading");
    setPrefillError("");
    try {
      const data = await prefillCrossExam(userId, { sessionId, note: note.trim(), documentIds });
      if (id !== reqRef.current) return;
      setPrefill(data);
      setForm({ ...fromPrefill(data), instructions: data?.values?.instructions || note.trim() });
      setStep("form");
    } catch (e) {
      if (id !== reqRef.current) return;
      setPrefillError(e.message || "Could not read the documents.");
      setForm((prev) => ({ ...prev, instructions: note.trim() }));
      setStep("form");
    }
  };

  const validate = () => {
    const next = {};
    if (!form.side) next.side = "Choose the side.";
    if (!form.witness.trim()) next.witness = "Enter the witness name.";
    if (!form.objective.trim()) next.objective = "Enter the objective.";
    const n = Number(form.numQuestions);
    if (!Number.isInteger(n) || n < MIN_Q || n > MAX_Q) next.numQuestions = `Enter a number between ${MIN_Q} and ${MAX_Q}.`;
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (step !== "form") return;
    if (!validate()) return;
    const prompt = buildCrossExamPrompt({ ...form, numQuestions: Number(form.numQuestions) }, formCfg);
    await onSubmit(prompt);
  };

  const docLine =
    documents.length === 1
      ? `1 document in this chat (${documents[0].name}).`
      : `${documents.length} documents in this chat (${documents
          .slice(0, 3)
          .map((d) => d.name)
          .join(", ")}${documents.length > 3 ? `, +${documents.length - 3} more` : ""}).`;

  const witnesses = prefill?.witnesses || [];
  const currentWitness = witnesses.find(
    (w) => (w.display || [w.label, w.name].filter(Boolean).join(" ")) === form.witness
  );

  return (
    <div className="edit-modal-overlay" onClick={close}>
      <form
        className={`cx-modal${step === "form" ? " cx-modal-wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
        role="dialog"
        aria-modal="true"
        aria-labelledby="cx-title"
      >
        <div className="cx-header">
          <h2 id="cx-title" className="cx-title">Prepare cross-examination</h2>
          <button type="button" className="cx-close" onClick={close} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* ── Step 1: intro ─────────────────────────────────────────────── */}
        {step === "intro" && (
          <>
            <div className="cx-body cx-intro">
              <h3 className="cx-intro-title">Step into cross-examination preparation?</h3>
              {hasDocs ? (
                <>
                  <p className="cx-intro-text">
                    {docLine} JuristBot can read them and fill in the form for you to check — or you can fill it in
                    yourself.
                  </p>
                  <label className="cx-field">
                    <span className="cx-label">Anything JuristBot should know first? (optional)</span>
                    <input
                      ref={noteRef}
                      className="cx-input"
                      placeholder="e.g. I act for the defendant; cross-examine DW-1"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          readDocuments();
                        }
                      }}
                    />
                  </label>
                </>
              ) : (
                <p className="cx-intro-text">
                  No documents in this chat yet. Upload the case records with{" "}
                  <FontAwesomeIcon icon={faFileLines} className="cx-note-icon" /> first for questions with sources — or
                  fill in the form yourself.
                </p>
              )}
            </div>
            <div className="cx-footer">
              <button type="button" className="cx-btn cx-btn-cancel" onClick={close}>Cancel</button>
              <button type="button" className={`cx-btn ${hasDocs ? "cx-btn-cancel" : "cx-btn-primary"}`} onClick={fillMyself}>
                Yes, I'll fill it in
              </button>
              {hasDocs && (
                <button type="button" className="cx-btn cx-btn-primary" onClick={readDocuments}>
                  Yes, read my documents
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Step 2: reading documents ─────────────────────────────────── */}
        {step === "reading" && (
          <>
            <div className="cx-body cx-reading">
              <div className="cx-spinner" aria-hidden="true" />
              <div>
                <strong>Reading your documents…</strong>
                <div className="cx-note">JuristBot is filling in the form for you to check. This takes about 15–30 seconds.</div>
              </div>
            </div>
            <div className="cx-footer">
              <button type="button" className="cx-btn cx-btn-cancel" onClick={close}>Cancel</button>
              <button type="button" className="cx-btn cx-btn-cancel" onClick={fillMyself}>
                Fill it in myself
              </button>
            </div>
          </>
        )}

        {/* ── Step 3: form ──────────────────────────────────────────────── */}
        {step === "form" && (
          <>
            <div className="cx-body">
              {prefillError && (
                <div className="cx-banner cx-banner-warn">
                  Couldn't read the documents ({prefillError}). Please fill in the form yourself.
                </div>
              )}
              {prefill?.prefilled && (
                <div className="cx-banner cx-banner-info">
                  <strong>Filled in from your documents — please check every field.</strong>
                  {prefill.case_type_reason && <div className="cx-note">{prefill.case_type_reason}</div>}
                  {Array.isArray(prefill.notes) && prefill.notes.length > 0 && (
                    <details className="cx-notes">
                      <summary>JuristBot's notes ({prefill.notes.length})</summary>
                      <ul>
                        {prefill.notes.map((n, i) => <li key={i}>{n}</li>)}
                      </ul>
                    </details>
                  )}
                </div>
              )}

              <div className="cx-row">
                <label className="cx-field">
                  <span className="cx-label">Case type</span>
                  <select ref={firstFieldRef} className="cx-input" value={form.caseType} onChange={changeCaseType}>
                    {CASE_TYPES.map((c) => (
                      <option key={c.key} value={c.key}>{c.label}</option>
                    ))}
                  </select>
                </label>
                <label className="cx-field">
                  <span className="cx-label">Side cross-examining</span>
                  <select
                    className={`cx-input${errors.side ? " has-error" : ""}`}
                    value={form.side}
                    onChange={set("side")}
                  >
                    <option value="">— choose —</option>
                    {formCfg.sides.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                  {errors.side && <span className="cx-error">{errors.side}</span>}
                </label>
              </div>

              <label className="cx-field">
                <span className="cx-label">Witness (as named in the records)</span>
                <input
                  className={`cx-input${errors.witness ? " has-error" : ""}`}
                  placeholder="PW-1 Ramesh / DW-1 …"
                  value={form.witness}
                  onChange={set("witness")}
                />
                {errors.witness && <span className="cx-error">{errors.witness}</span>}
                {witnesses.length > 0 && (
                  <div className="cx-chips">
                    <span className="cx-chips-label">Found in the records:</span>
                    {witnesses.map((w, i) => {
                      const display = w.display || [w.label, w.name].filter(Boolean).join(" ");
                      return (
                        <button
                          type="button"
                          key={`${display}-${i}`}
                          className={`cx-chip${form.witness === display ? " active" : ""}`}
                          title={[w.role, w.source].filter(Boolean).join(" · ")}
                          onClick={() => pickWitness(w)}
                        >
                          {display}
                          {w.witness_type && <span className="cx-chip-meta"> · {w.witness_type}</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </label>

              <label className="cx-field">
                <span className="cx-label">Witness type</span>
                <select className="cx-input" value={form.witnessType} onChange={set("witnessType")}>
                  {(formCfg.witness_types.includes(form.witnessType)
                    ? formCfg.witness_types
                    : [form.witnessType, ...formCfg.witness_types]
                  ).map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label className="cx-field">
                <span className="cx-label">Objective</span>
                <textarea
                  rows={2}
                  className={`cx-input cx-textarea cx-textarea-sm${errors.objective ? " has-error" : ""}`}
                  placeholder="e.g. show the time of the incident is inconsistent"
                  value={form.objective}
                  onChange={set("objective")}
                />
                {errors.objective && <span className="cx-error">{errors.objective}</span>}
                {currentWitness?.objectives?.length > 1 && (
                  <div className="cx-chips cx-chips-col">
                    <span className="cx-chips-label">Other objectives from the records:</span>
                    {currentWitness.objectives.map((o, i) => (
                      <button
                        type="button"
                        key={i}
                        className={`cx-chip cx-chip-wrap${form.objective === o ? " active" : ""}`}
                        onClick={() => setForm((prev) => ({ ...prev, objective: o }))}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                )}
              </label>

              <label className="cx-field">
                <span className="cx-label">Case theory (optional)</span>
                <textarea
                  className="cx-input cx-textarea"
                  placeholder="e.g. the witness was not present at the spot"
                  value={form.caseTheory}
                  onChange={set("caseTheory")}
                />
              </label>

              <div className="cx-row">
                <label className="cx-field">
                  <span className="cx-label">Case name (optional)</span>
                  <input className="cx-input" placeholder="State v. … / A v. B" value={form.caseName} onChange={set("caseName")} />
                </label>
                <label className="cx-field">
                  <span className="cx-label">Case number (optional)</span>
                  <input className="cx-input" placeholder="C.C. No. … / CS No. …" value={form.caseNumber} onChange={set("caseNumber")} />
                </label>
              </div>

              <div className="cx-row">
                <label className="cx-field">
                  <span className="cx-label">Number of questions</span>
                  <input
                    type="number"
                    min={MIN_Q}
                    max={MAX_Q}
                    className={`cx-input${errors.numQuestions ? " has-error" : ""}`}
                    value={form.numQuestions}
                    onChange={set("numQuestions")}
                  />
                  {errors.numQuestions && <span className="cx-error">{errors.numQuestions}</span>}
                </label>
                <label className="cx-field">
                  <span className="cx-label">Style</span>
                  <select className="cx-input" value={form.style} onChange={set("style")}>
                    {STYLES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                </label>
              </div>

              <fieldset className="cx-field cx-fieldset">
                <legend className="cx-label">Headings (optional — all if none ticked)</legend>
                <div className="cx-checks">
                  {formCfg.headings.map((h) => (
                    <label key={h} className="cx-check">
                      <input type="checkbox" checked={form.headings.includes(h)} onChange={() => toggleHeading(h)} />
                      <span>{h}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <label className="cx-field">
                <span className="cx-label">Other details for JuristBot (optional — facts not in the documents)</span>
                <textarea
                  className="cx-input cx-textarea"
                  placeholder="e.g. He gave a power of attorney in favour of the plaintiff and received the money"
                  value={form.instructions}
                  onChange={set("instructions")}
                />
              </label>

              <p className="cx-note">
                Questions are drafted from the documents in this chat (
                <FontAwesomeIcon icon={faFileLines} className="cx-note-icon" />
                ). Every question must be reviewed by an advocate before use.
              </p>
            </div>

            <div className="cx-footer">
              <button type="button" className="cx-btn cx-btn-cancel" onClick={close}>Cancel</button>
              <button type="submit" className="cx-btn cx-btn-primary">Prepare questions</button>
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default CrossExamModal;